import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Tabs,
  Tab,
} from "@mui/material";
import { Responsive, WidthProvider } from "react-grid-layout";
// import WidgetForm from "../Widget/WidgetForm";
// C:\Users\sesa796069\Documents\project\Operate_Github\Frontend\SmartComm_Operate_ENERGY_Client\Proprietary\src\pages\ConfigPages\DashboardConfigurator\Dashboard\Widget\WidgetForm.js
import CustomDialogBox from "../../../CommonComponents/CustomDialogBox";
import {
  updatedashboardwidgetlist,
  fetchDataForWidgets,
  livedata,
} from "../../../../Restclient/datacollection";
import { updatelivedata } from "../../../../store/mainpage_data";

import CustomCancelButton from "../../../CommonComponents/CustomCancelButton";
import CustomButton from "../../../CommonComponents/CustomButton";
import WidgetForm from "./Widget/WidgetForm";
import WidgetCard from "./Widget/utils/WidgetCard";
import { isChartType, isGaugeType, isTableType } from "./Widget/utils/widgetTypes";

export default function DashboardWidgetsPage({
  dashboard,
  onBack,
  existingWidgets = [],
  onPersist,
}) {
  const dispatch = useDispatch();

  // Redux period label (date selection)
  const periodLabel = useSelector(
    (state) => state.mainpagedata?.selecteddate || state.selecteddate
  );
  // Theme selector
  const theme = useSelector((state) => state.themedata?.theme || "light");

  // Local state
  const [widgets, setWidgets] = useState([]);
  const [arrangeMode, setArrangeMode] = useState(false);
  const [layout, setLayout] = useState([]); // [{i,x,y,w,h}]
  const [baselineLayout, setBaselineLayout] = useState([]);
  const [layoutDirty, setLayoutDirty] = useState(false);
  // Guard to ensure we ignore all automatic layout churn on first mount (including react-grid-layout compaction)
  const [initialized, setInitialized] = useState(false);
  // Track if user has actually interacted (dragged/resized) to start dirty tracking
  const [userLayoutTouched, setUserLayoutTouched] = useState(false);
  // Captures whether the last arrange session produced a layout change (used to enable Save All after exit)
  const [arrangementDirty, setArrangementDirty] = useState(false);
  const ResponsiveGridLayout = React.useMemo(
    () => WidthProvider(Responsive),
    []
  );
  const [baselineWidgets, setBaselineWidgets] = useState([]);
  const [widgetData, setWidgetData] = useState({}); // { widgetId: series[] }
  const liveTagBufferRef = useRef({}); // { tagid: [{timestamp,value},...] }
  const LIVE_BUFFER_MAX = 120; // keep last 120 points (~2 min if 1s sampling)
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isLiveFetching, setIsLiveFetching] = useState(false);
  const [widgetFormOpen, setWidgetFormOpen] = useState(false);
  const [editingWidget, setEditingWidget] = useState(null);
  const widgetFormRef = useRef(null);
  const [hasSelectedSubWidget, setHasSelectedSubWidget] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({
    dialogTitle: "",
    dialogText: "",
    confirmText: "OK",
    cancelText: "Cancel",
    type: "Info",
    handleConfirmDialog: () => setDialogOpen(false),
    handleCloseDialog: () => setDialogOpen(false),
  });
  const [dateRange, setDateRange] = useState({}); // Placeholder if extended dateRange is added later
  // Hide non-essential controls while arranging until layout saved
  const [controlsHidden, setControlsHidden] = useState(false);
  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Ingest existing widgets when provided (edit mode)
  useEffect(() => {
    if (existingWidgets && existingWidgets.length) {
      const normalized = existingWidgets.map((w) => {
        const base = w.props || {
          basic: w.basicProps || {},
          extended: w.extendedProps || {},
        };
        const extOriginal = base.extended || {};
        // Lift isConfig out of extended if it was stored there
        const legacyIsConfig =
          extOriginal.isConfig ?? base.isConfig ?? w.isConfig;
        if (extOriginal.isConfig) delete extOriginal.isConfig;
        const metaSource = extOriginal.gridmeta ||
          w.gridmeta || {
            w: w.w ?? w.width ?? w.grid_w,
            h: w.h ?? w.height ?? w.grid_h,
            x: w.x ?? w.grid_x,
            y: w.y ?? w.grid_y,
          };
        const gridmeta = {};
        ["w", "h", "x", "y"].forEach((k) => {
          if (Number.isFinite(metaSource?.[k])) gridmeta[k] = metaSource[k];
        });
        return {
          id: String(w.id || w.widgetid || `${Date.now()}_${Math.random()}`),
          widgetName:
            w.widgetName ||
            w.widgetname ||
            base.basic?.widgetName ||
            base.basic?.title ||
            "Widget",
          type: w.type,
          props: {
            isConfig: legacyIsConfig === undefined ? true : !!legacyIsConfig,
            basic: base.basic || {},
            extended: { ...extOriginal, gridmeta },
          },
        };
      });
      setWidgets(normalized);
      setBaselineWidgets(normalized);
      // Reset layout & baselines so persisted sizes are applied deterministically
      setLayout([]);
      setBaselineLayout([]);
    }
  }, [existingWidgets]);

  // Rebuild layout entirely when widgets change (order preserved, persisted sizes used)
  useEffect(() => {
    if (!widgets.length) {
      setLayout([]);
      return;
    }
    let storedMap = {};
    try {
      const raw = localStorage.getItem(`dashboard_layout_${dashboard?.id}`);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr))
          storedMap = arr.reduce((m, l) => {
            if (l?.i) m[String(l.i)] = l;
            return m;
          }, {});
      }
    } catch (_) {}
    // Determine whether we must generate positions (packing) or can trust persisted ones
    const MAX_COLS = 4; // base column count (lg breakpoint)
    const MUST_PACK = widgets.some((w) => {
      const meta = w.props?.extended?.gridmeta || storedMap[String(w.id)] || {};
      return !Number.isFinite(meta.x) || !Number.isFinite(meta.y);
    });

    let next;
    if (!MUST_PACK) {
      // Use persisted positions as-is; DO NOT rearrange
      next = widgets.map((w) => {
        const meta =
          w.props?.extended?.gridmeta || storedMap[String(w.id)] || {};
        const wVal = Number.isFinite(meta.w) ? meta.w : 1;
        const hVal = Number.isFinite(meta.h) ? meta.h : 2;
        return {
          i: String(w.id),
          x: meta.x,
          y: meta.y,
          w: wVal,
          h: hVal,
          minW: 1,
          minH: 2,
        };
      });
    } else {
      // Packing algorithm only for first-time / missing coordinates
      let cursorX = 0;
      let cursorY = 0;
      next = widgets.map((w) => {
        const meta =
          w.props?.extended?.gridmeta || storedMap[String(w.id)] || {};
        let wVal = Number.isFinite(meta.w) ? meta.w : 1;
        let hVal = Number.isFinite(meta.h) ? meta.h : 2;
        if (wVal < 1) wVal = 1;
        if (wVal > MAX_COLS) wVal = MAX_COLS;
        if (wVal > MAX_COLS - cursorX) {
          cursorX = 0;
          cursorY += 1;
        }
        const item = {
          i: String(w.id),
          x: cursorX,
          y: cursorY,
          w: wVal,
          h: hVal,
          minW: 1,
          minH: 2,
        };
        cursorX += wVal;
        if (cursorX >= MAX_COLS) {
          cursorX = 0;
          cursorY += 1;
        }
        return item;
      });
    }
    setLayout(next);
  }, [widgets, dashboard?.id]);

  // Capture baseline layout once
  useEffect(() => {
    if (!baselineLayout.length && layout.length && baselineWidgets.length) {
      setBaselineLayout(layout.map((l) => ({ ...l })));
      // Once we have captured the first stable baseline snapshot, mark initialized after a microtask
      Promise.resolve().then(() => setInitialized(true));
    }
  }, [layout, baselineLayout.length, baselineWidgets.length]);

  const handleLayoutChange = (current) => {
    setLayout(current);
    // Ignore layout change noise until initialized AND user has interacted (covers initial mount + compaction & implicit recalcs)
    if (!initialized || !baselineLayout.length || !userLayoutTouched) {
      setLayoutDirty(false);
      return;
    }
    const baseMap = baselineLayout.reduce((m, l) => {
      m[l.i] = l;
      return m;
    }, {});
    const changed =
      current.some((l) => {
        const b = baseMap[l.i];
        if (!b) return true;
        return b.w !== l.w || b.h !== l.h || b.x !== l.x || b.y !== l.y;
      }) || current.length !== baselineLayout.length;
    setLayoutDirty(changed);
  };

  // When the user first touches layout (drag/resize stop), recompute dirtiness using current layout
  useEffect(() => {
    if (userLayoutTouched && initialized && baselineLayout.length) {
      const baseMap = baselineLayout.reduce((m, l) => {
        m[l.i] = l;
        return m;
      }, {});
      const changed =
        layout.some((l) => {
          const b = baseMap[l.i];
          if (!b) return true;
          return b.w !== l.w || b.h !== l.h || b.x !== l.x || b.y !== l.y;
        }) || layout.length !== baselineLayout.length;
      setLayoutDirty(changed);
    }
  }, [userLayoutTouched, initialized, baselineLayout, layout]);

  // Track dirtiness vs baseline snapshot
  useEffect(() => {
    let widgetsChanged = false;
    try {
      widgetsChanged =
        JSON.stringify(widgets) !== JSON.stringify(baselineWidgets);
    } catch {
      widgetsChanged = widgets.length !== baselineWidgets.length;
    }
    // Only consider layoutDirty once baselineLayout has been set at least once
    const effectiveLayoutDirty =
      baselineLayout.length && initialized && userLayoutTouched
        ? layoutDirty
        : false;
    setIsDirty(widgetsChanged || effectiveLayoutDirty);
  }, [
    widgets,
    baselineWidgets,
    layoutDirty,
    baselineLayout.length,
    initialized,
    userLayoutTouched,
  ]);

  // Arrange mode entry/exit lifecycle
  useEffect(() => {
    if (arrangeMode) {
      // Entering arrange mode: reset interaction & previous arrangement flag
      setUserLayoutTouched(false);
      setLayoutDirty(false);
      setArrangementDirty(false);
      setControlsHidden(true);
    } else {
      // Exiting arrange mode: compact layout to fill gaps before marking dirty
      if (layout.length) {
        const packed = packLayout(layout);
        const changedByPack = hasLayoutChanged(layout, packed);
        if (changedByPack) {
          setLayout(packed);
        }
        // Mark arrangement dirty if user changed or packing altered layout
        setArrangementDirty(
          (layoutDirty && userLayoutTouched && initialized) || changedByPack
        );
        // Keep controls hidden if there are changes needing save
        setControlsHidden(
          (layoutDirty && userLayoutTouched && initialized) || changedByPack
        );
      } else {
        setArrangementDirty(false);
        setControlsHidden(false);
      }
    }
  }, [arrangeMode]);

  // Utility: determine if two layouts differ
  const hasLayoutChanged = (a, b) => {
    if (a.length !== b.length) return true;
    const mapA = a.reduce((m, l) => {
      m[l.i] = l;
      return m;
    }, {});
    for (const item of b) {
      const prev = mapA[item.i];
      if (
        !prev ||
        prev.x !== item.x ||
        prev.y !== item.y ||
        prev.w !== item.w ||
        prev.h !== item.h
      )
        return true;
    }
    return false;
  };

  // Utility: pack layout filling gaps row by row preserving order
  const packLayout = (current) => {
    const MAX_COLS = 4; // same as lg columns
    // Sort by y then x to process in visual order
    const sorted = [...current].sort((a, b) =>
      a.y === b.y ? a.x - b.x : a.y - b.y
    );
    const grid = []; // grid[y][x] occupancy
    const placeItem = (item) => {
      // Try from y=0 upwards to find first spot where width fits contiguous free cells
      let y = 0;
      while (true) {
        if (!grid[y]) grid[y] = new Array(MAX_COLS).fill(null);
        for (let x = 0; x < MAX_COLS; x++) {
          // ensure room horizontally
          if (x + item.w > MAX_COLS) break;
          // check free cells
          let free = true;
          for (let dx = 0; dx < item.w; dx++) {
            if (grid[y][x + dx]) {
              free = false;
              break;
            }
          }
          if (!free) continue;
          // found spot
          for (let dy = 0; dy < item.h; dy++) {
            if (!grid[y + dy]) grid[y + dy] = new Array(MAX_COLS).fill(null);
            for (let dx = 0; dx < item.w; dx++) grid[y + dy][x + dx] = item.i;
          }
          return { ...item, x, y };
        }
        y++;
      }
    };
    const placed = sorted.map(placeItem);
    // Maintain original order mapping by id
    const placedMap = placed.reduce((m, l) => {
      m[l.i] = l;
      return m;
    }, {});
    return current.map((l) => placedMap[l.i]);
  };

  // Warn about unsaved changes on unload
  useEffect(() => {
    const handler = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // Navigation guard helper
  const attemptNavigate = (action) => {
    if (isDirty) {
      setDialogConfig({
        dialogTitle: "Unsaved Changes",
        dialogText: "Leave without saving?",
        confirmText: "OK",
        cancelText: "Cancel",
        type: "Info",
        handleConfirmDialog: () => {
          setDialogOpen(false);
          action && action();
        },
        handleCloseDialog: () => setDialogOpen(false),
      });
      setDialogOpen(true);
    } else {
      action && action();
    }
  };

  // Widget CRUD handlers
  const handleManageWidgets = () => {
    setEditingWidget(null);
    setHasSelectedSubWidget(false);
    setWidgetFormOpen(true);
  };
  const handleEditWidget = (w) => {
    setEditingWidget(w);
    setHasSelectedSubWidget(Boolean(w?.type));
    setWidgetFormOpen(true);
  };
  const handleDeleteWidget = (w) => {
    setWidgets((prev) => prev.filter((x) => x.id !== w.id));
    setWidgetData((prev) => {
      const clone = { ...prev };
      delete clone[w.id];
      return clone;
    });
  };
  const handleCloneWidget = (w) => {
    const clone = JSON.parse(JSON.stringify(w));
    clone.id = `${w.id}_clone_${Date.now()}`;
    clone.widgetName = `${w.widgetName || "Widget"} (Copy)`;
    setWidgets((prev) => [...prev, clone]);
  };

  const handleWidgetSave = (saved) => {
    // Rebuild tagprops from datapoints so edits persist
    const ext = saved.props?.extended || {};
    const datapoints = Array.isArray(ext.datapoints) ? ext.datapoints : [];
    const rebuiltTagProps = datapoints.map((dp) => ({
      tagid: dp.tagid || dp.key || dp.id,
      tagname:
        dp.tagname ||
        dp.datapointName ||
        dp.name ||
        dp.displayName ||
        dp.tagid ||
        dp.key ||
        "Tag",
      color: dp.color || dp.backgroundColor || dp.textColor,
    }));
    // Remove any legacy isConfig inside extended and ensure it exists at props root
    const updatedExtended = { ...ext, tagprops: rebuiltTagProps };
    if (updatedExtended.isConfig) 
      delete updatedExtended.isConfig;
    const updated = {
      ...saved,
      props: {
        isConfig: true,
        basic: saved.props?.basic || {},
        extended: updatedExtended,
      },
    };
    // eslint-disable-next-line no-console

    setWidgets((prev) => {
      const exists = prev.find((w) => w.id === updated.id);
      return exists
        ? prev.map((w) => (w.id === updated.id ? updated : w))
        : [...prev, updated];
    });
    setWidgetFormOpen(false);
    setEditingWidget(null);
  };

  // Save all widgets to backend
  const handleSaveAll = () => {
    if (!dashboard?.id) {
      setSaveError("Dashboard id missing");
      return;
    }
    const missing = widgets.filter((w) => !w.id || !w.type);
    if (missing.length) {
      setSaveError(
        `Cannot save: ${missing.length} widget(s) missing ${
          missing[0].id ? "type" : "id"
        }.`
      );
      return;
    }
    setSaving(true);
    setSaveError("");
    const layoutMap = layout.reduce((m, l) => {
      m[l.i] = l;
      return m;
    }, {});
    const outbound = widgets.map((w) => {
      const l = layoutMap[String(w.id)];
      const ext = w.props?.extended || {};
      const extended = {
        ...ext,
        gridmeta: l ? { w: l.w, h: l.h, x: l.x, y: l.y } : ext.gridmeta || {},
      };
      return {
        id: w.id,
        widgetname: w.widgetName,
        type: w.type,
        w: extended.gridmeta?.w,
        h: extended.gridmeta?.h,
        x: extended.gridmeta?.x,
        y: extended.gridmeta?.y,
        props: {
          isConfig: !!w.props?.isConfig,
          basic: w.props?.basic || {},
          extended,
        },
      };
    });
    // eslint-disable-next-line no-console
    // console.log(
    //   "[DashboardWidgetsPage] Saving widgets payload:",
    //   JSON.parse(JSON.stringify(outbound))
    // );
    updatedashboardwidgetlist(dashboard.id, outbound, (res) => {
      setSaving(false);
      const rawStatus = res?.status;
      const status =
        typeof rawStatus === "string" ? rawStatus.toLowerCase() : "";
      console.log("Status:", status);
      // Treat multiple backend success tokens as success
      const isSuccess = ["success", "done", "ok", "updated"].includes(status);
      // eslint-disable-next-line no-console
      console.log("[DashboardWidgetsPage] Save response:", res?.data);
      if (isSuccess) {
        const updatedLocal = widgets.map((w) => {
          const l = layoutMap[String(w.id)];
          if (!l) return w;
          return {
            ...w,
            props: {
              ...w.props,
              extended: {
                ...(w.props?.extended || {}),
                gridmeta: { w: l.w, h: l.h, x: l.x, y: l.y },
              },
            },
          };
        });
        setWidgets(updatedLocal);
        setBaselineWidgets(updatedLocal);
        setBaselineLayout(layout.map((l) => ({ ...l })));
        setLayoutDirty(false);
        setArrangementDirty(false);
        setSaveError("");
        onPersist && onPersist(outbound);
        // Persist layout to localStorage fallback
        try {
          localStorage.setItem(
            `dashboard_layout_${dashboard?.id}`,
            JSON.stringify(
              layout.map((l) => ({ i: l.i, x: l.x, y: l.y, w: l.w, h: l.h }))
            )
          );
        } catch (_) {}
        // Reveal controls after successful save
        setControlsHidden(false);
      } else {
        setSaveError(res?.data?.message || "Save failed");
      }
    });
  };

  // Build live series from buffered tag data ensuring chronological order & window
  const buildLiveSeriesForWidget = (widget) => {
    const ext = widget.props?.extended || {};
    const source =
      Array.isArray(ext.datapoints) && ext.datapoints.length
        ? ext.datapoints
        : ext.tagprops || [];
    const series = [];
    source.forEach((item) => {
      const tagid = item.tagid || item.key || item.id;
      if (!tagid) return;
      const tagname = item.tagname || item.datapointName || item.name || tagid;
      const buf = liveTagBufferRef.current[tagid];
      if (Array.isArray(buf) && buf.length) {
        const points = buf
          .map((p) => ({ x: p.timestamp, y: p.value }))
          .filter(
            (p) =>
              p.x !== undefined &&
              p.x !== null &&
              p.y !== undefined &&
              p.y !== null &&
              isFinite(p.y)
          );
        if (points.length)
          series.push({
            tagid,
            name: tagname,
            points,
            color: item.color || item.textColor || item.backgroundColor,
            fetchSource: "live",
          });
      }
    });
    return series;
  };

  // Incorporate new live records into buffer
  const ingestLivePayload = (rawArray) => {
    if (!Array.isArray(rawArray)) return;
    rawArray.forEach((rec) => {
      const tagid = rec.TagId || rec.tagid || rec.TagID;
      if (!tagid) return;
      const ts = rec.timestamp || rec.time || Date.now();
      const value = rec.value ?? rec.val ?? rec.y;
      if (value === undefined || value === null || !isFinite(value)) return;
      const buf = liveTagBufferRef.current[tagid] || [];
      buf.push({
        timestamp:
          typeof ts === "number"
            ? ts.toString().length <= 11
              ? ts * 1000
              : ts
            : Date.now(),
        value,
      });
      // Trim buffer
      if (buf.length > LIVE_BUFFER_MAX)
        buf.splice(0, buf.length - LIVE_BUFFER_MAX);
      liveTagBufferRef.current[tagid] = buf;
    });
  };

   // Test widgets: live + historic separation
  const handleTestWidgets = async () => {
    if (isTesting) return;
    setIsTesting(true);
    setSaveError("");
    // eslint-disable-next-line no-console
    // console.log(
    //   "[DashboardWidgetsPage] Test start. Widget definitions:",
    //   JSON.parse(JSON.stringify(widgets))
    // );
    const liveWidgets = widgets.filter((w) => {
      const dt = (w.props?.extended?.dataType || "live").toLowerCase();
      return dt === "live" || dt === "mixed";
    });
    const historicWidgets = widgets.filter((w) => {
      const dt = (w.props?.extended?.dataType || "live").toLowerCase();
      return dt === "historic" || dt === "mixed";
    });
    const newData = {};

    // Live fetch (single call)
    if (liveWidgets.length) {
      setIsLiveFetching(true);
      // eslint-disable-next-line no-console
      const allTagIds = liveWidgets.flatMap((w) => {
        const ext = w.props?.extended || {};
        const source =
          Array.isArray(ext.datapoints) && ext.datapoints.length
            ? ext.datapoints
            : ext.tagprops || [];
        return source
          .map((item) => String(item.tagid || item.key || item.id))
          .filter(Boolean);
      });

      await new Promise((resolve) => {
        livedata({ tagids: allTagIds, overviewbarparameters: [] }, (res) => {
          try {
            const raw = res?.data?.data;
            const parsedArr = typeof raw === "string" ? JSON.parse(raw) : raw;
            ingestLivePayload(parsedArr);
            // Rebuild series per widget from buffer
            liveWidgets.forEach((w) => {
              const ext = w.props?.extended || {};
              const dt = (ext.dataType || "live").toLowerCase();
              if (dt === "mixed") {
                const source = Array.isArray(ext.datapoints) && ext.datapoints.length
                  ? ext.datapoints
                  : ext.tagprops || [];
                const liveDps = source.filter(
                  (dp) => (dp?.dataType || "live").toLowerCase() === "live"
                );
                const virtualWidget = {
                  ...w,
                  props: { ...w.props, extended: { ...ext, datapoints: liveDps } },
                };
                newData[w.id] = buildLiveSeriesForWidget(virtualWidget);
              } else {
                newData[w.id] = buildLiveSeriesForWidget(w);
              }
            });
            dispatch(updatelivedata(parsedArr));
          } catch (err) {
            console.error("Live data parse error", err);
          } finally {
            setIsLiveFetching(false);
            resolve();
          }
        });
      });
    }

    // Historic fetch (historic + mixed widgets)
    if (historicWidgets.length) {
      const globalStart =
        dateRange?.startdates ||
        dateRange?.start ||
        new Date(Date.now() - 3600 * 1000).toISOString();
      const globalEnd =
        dateRange?.enddates || dateRange?.end || new Date().toISOString();
      // eslint-disable-next-line no-console
      // console.log("[DashboardWidgetsPage] Fetching historic data for range:", {
      //   globalStart,
      //   globalEnd,
      //   periodLabel,
      //   historicCount: historicWidgets.length,
      // });
      try {
        const processedHistoricWidgets = historicWidgets.map((w) => {
          const ext = w.props?.extended || {};
          const dt = (ext.dataType || "live").toLowerCase();
          if (dt !== "mixed") return w;
          const source = Array.isArray(ext.datapoints) && ext.datapoints.length
            ? ext.datapoints
            : ext.tagprops || [];
          const historicDps = source.filter(
            (dp) => (dp?.dataType || "live").toLowerCase() === "historic"
          );
          return {
            ...w,
            props: { ...w.props, extended: { ...ext, datapoints: historicDps } },
          };
        });
        const { widgetData: fetched } = await fetchDataForWidgets(
          processedHistoricWidgets,
          globalStart,
          globalEnd,
          periodLabel || ""
        );
        Object.keys(fetched).forEach((k) => {
          const existing = Array.isArray(newData[k]) ? newData[k] : [];
          newData[k] = [...existing, ...(fetched[k] || [])];
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Historic fetch error", e);
        setSaveError(e.message || "Historic fetch failed");
      }
    }

    setWidgetData((prev) => ({ ...prev, ...newData }));
    setIsTesting(false);
  };


  {
    /* Reusable button style matching "Manage Groups" look */
  }
  const buttonStyle = {

    marginLeft: "5px",
    // marginRight: '8px'
  };
  return (
    <>
    <Box sx={{ width: "100%", height: "100%" }}>
      {/* Top-right buttons */} 
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Box
        sx={{
          p: 1,
          color: "#008cd7ff",
          fontWeight: "700",
          height: "23px",
          fontFamily: "Roboto",
        }}
      >
          {dashboard?.name} - Widgets

      </Box>

      <Box>

        {!controlsHidden && (
          <>
                      <CustomButton
              onClick={() => attemptNavigate(onBack)}
              buttonText={"Dashboard List"}
              disabled={arrangeMode}
              sx={{
                ...buttonStyle,
                opacity: arrangeMode ? 0.5 : 1,
                pointerEvents: arrangeMode ? "none" : "auto",
              }}
            />
            <CustomButton
              onClick={handleManageWidgets}
              buttonText={"Manage Widgets"}
              disabled={arrangeMode}
              sx={{
                ...buttonStyle,
                opacity: arrangeMode ? 0.5 : 1,
                pointerEvents: arrangeMode ? "none" : "auto",
              }}
            />

            <CustomButton
              onClick={handleTestWidgets}
              buttonText={isTesting
                ? isLiveFetching
                  ? "Live & Historic..."
                  : "Testing..."
                : "Test Widgets"}
              disabled={isTesting || arrangeMode}
              sx={{
                ...buttonStyle,
                opacity: isTesting || arrangeMode ? 0.5 : 1,
                pointerEvents: isTesting || arrangeMode ? "none" : "auto",
              }}
            />
          </>
        )}
        <CustomButton
          onClick={() => setArrangeMode((m) => !m)}
          buttonText={arrangeMode ? "Arranged" : "Arrange"}
          sx={buttonStyle}
        />
        <CustomButton
          onClick={handleSaveAll}
          buttonText={saving ? "Saving..." : isDirty ? "Save All" : "All Saved"}
          disabled={saving || arrangeMode || !isDirty}
          sx={{
            ...buttonStyle,
            backgroundColor: saving || arrangeMode || !isDirty
              ? (theme === 'dark' ? "#444B53" : "#C1C1C180")
              : (theme === 'dark' ? "#434248" : "var(--LK_BrandBlue)"),
            color: saving || arrangeMode || !isDirty
              ? (theme === 'dark' ? "#444B53" : "##7E7E7E")
              : (theme === 'dark' ? "#7E7E7E" : "var(--White)"),
            cursor: saving || arrangeMode || !isDirty ? "not-allowed" : "pointer",
          }}
        />
        
      </Box>

      </Box>


    </Box>
      <Box
        sx={{
          backgroundColor: "var(--SecondaryBackground)"       
        }}
      >
        {saveError && (
          <Typography
            color="error"
            sx={{ mb: 2, color: theme === "light" ? "#3C3C3C" : "#FFFFFF" }}
          >
            {saveError}
          </Typography>
        )}
        <Box
          sx={{
            backgroundColor: "var(--PrimaryBackground)",
            height: "calc(100vh - 385px)",
            width: "100%",
            overflowY: "auto",
            p: 0.5,
          }}
        >
        {widgets.length ? (
          <ResponsiveGridLayout
            className="layout"
            rowHeight={160}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{ lg: 4, md: 3, sm: 2, xs: 1, xxs: 1 }}
            margin={[16, 16]}
            isResizable={arrangeMode}
            isDraggable={arrangeMode}
            layout={layout}
            onLayoutChange={handleLayoutChange}
            draggableHandle={arrangeMode ? ".widget-drag-handle" : undefined}
            onDragStop={() => {
              setUserLayoutTouched(true);
              if (arrangeMode) {
                setLayout((prev) => {
                  const packed = packLayout(prev);
                  return packed;
                });
              }
            }}
            onResizeStop={() => {
              setUserLayoutTouched(true);
              if (arrangeMode) {
                setLayout((prev) => {
                  const packed = packLayout(prev);
                  return packed;
                });
              }
            }}
          >
            {widgets.map((widget) => (
              <div
                key={String(widget.id)}
                data-grid={layout.find((l) => l.i === String(widget.id))}
                style={{ display: "flex" }}
              >
                <WidgetCard
                  widget={widget}
                  onEdit={handleEditWidget}
                  onDelete={handleDeleteWidget}
                  onClone={handleCloneWidget}
                  
                  showPreviewGraph={
                    widget.type &&
                    (isChartType(widget.type) || 
                     isGaugeType(widget.type) || 
                     isTableType(widget.type))
                  }
                  data={widgetData[widget.id] || []}
                  readOnly={arrangeMode}
                />
              </div>
            ))}
          </ResponsiveGridLayout>
        ) : (
          <Typography
            variant="body1"
            style={{ color: theme === "light" ? "#3C3C3C" : "#FFFFFF" }}
          >
            No widgets found for this dashboard.
          </Typography>
        )}
        </Box>
      </Box>

      <Dialog
        open={widgetFormOpen}
        onClose={() => setWidgetFormOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{ background: theme === "light" ? "#FFFFFF" : "#1E1E1E" }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ color: "#008CD7" }}
          >
            {editingWidget ? "Edit Widget" : "Manage Widgets"}  {editingWidget?.type ?  "-" +editingWidget?.type : ""}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {!editingWidget && hasSelectedSubWidget && (
                <CustomButton
                  buttonText={"Widgets List"}
                  onClick={() => widgetFormRef.current?.backToManageWidgets?.()}
                />
              )}
              {hasSelectedSubWidget && (
                <CustomButton
                  buttonText={"Save"}
                  onClick={() => widgetFormRef.current?.saveWidget?.()}
                />
              )}
              <CustomCancelButton
                sx={{ color: "#7E7E7E" }}
                buttonText={"Close"}
                onClick={() => setWidgetFormOpen(false)}
              />
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{ background: theme === "light" ? "#FFFFFF" : "#1E1E1E" }}
        >
          <WidgetForm
            ref={widgetFormRef}
            onWidgetSave={handleWidgetSave}
            initialWidget={editingWidget}
            showBottomActions={false}
            onSubWidgetSelectionChange={setHasSelectedSubWidget}
          />
        </DialogContent>
      </Dialog>

      <CustomDialogBox
        dialogTitle={dialogConfig.dialogTitle}
        dialogText={dialogConfig.dialogText}
        openDialog={dialogOpen}
        handleCloseDialog={dialogConfig.handleCloseDialog}
        handleConfirmDialog={dialogConfig.handleConfirmDialog}
        confirmText={dialogConfig.confirmText}
        cancelText={dialogConfig.cancelText}
        type={dialogConfig.type}
      />
    </>
  );
}
